import isEmpty from "lodash/isEmpty";
import {useEffect, useState} from "react";
import {NavigationType, useLocation, useNavigationType, useSearchParams} from "react-router";

import {FilterCategory, FilterSets} from "@/components/filterDropdown/FilterDropdown.ts";

export const DEFAULT_FILTERS_KEY = "filters";

/** URL filters win over stored ones — shared with deep-link rewrites that merge into what a list would load. */
export const resolveFilterSets = (urlFiltersValue: string, storageKey?: string): FilterSets<FilterCategory> => {
  try {
    const parsed: unknown = JSON.parse(urlFiltersValue || (storageKey && localStorage.getItem(storageKey)) || "");
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as FilterSets<FilterCategory>)
      : {};
  } catch {
    return {};
  }
};

export interface UseFilterSetsOptions {
  filtersKey?: string;
  sessionStorageKey?: string;
  useUrlParams?: boolean;
  resetPaginationOnChange?: boolean;
  onFilterSetChange?: (filterSets: FilterSets<FilterCategory>, params: URLSearchParams) => void;
}

export const useFilterSets = (options: UseFilterSetsOptions = {}) => {
  const {filtersKey = DEFAULT_FILTERS_KEY, sessionStorageKey, useUrlParams = true, onFilterSetChange} = options;

  const [queryParams, setQueryParams] = useSearchParams();
  const {key: locationKey} = useLocation();
  const navigationType = useNavigationType();
  const urlFiltersValue = (useUrlParams && queryParams.get(filtersKey)) || "";

  const [filterSets, setFilterSetsState] = useState<FilterSets<FilterCategory>>(() =>
    resolveFilterSets(urlFiltersValue, sessionStorageKey)
  );
  // Filters restored from storage are not in the URL yet. The first URL sync writes them there and so replaces the
  // location they were restored on, which ends the restore.
  const [restoredOnLocationKey] = useState(() =>
    useUrlParams && !urlFiltersValue && !isEmpty(filterSets) ? locationKey : undefined
  );
  const isRestoringStoredFilterSets = restoredOnLocationKey === locationKey;

  useEffect(() => {
    if (!useUrlParams) {
      return;
    }
    if (!urlFiltersValue && !isEmpty(filterSets)) {
      // Write restored filters to the URL instead of letting the empty URL clear them. It is a filter change, so the
      // list starts on page 1: a page in the URL was for the unfiltered list.
      if (isRestoringStoredFilterSets) {
        const params = new URLSearchParams(queryParams);
        params.set(filtersKey, JSON.stringify(filterSets));
        onFilterSetChange?.(filterSets, params);
        setQueryParams(params, {replace: true});
        return;
      }
      // Only Back and Forward clear them. A link to the bare list keeps them, and so does a tab switch, which unmounts
      // this list next: writing the URL or storage here would hand its filters to the next tab.
      if (navigationType !== NavigationType.Pop) {
        return;
      }
    }
    const urlFilterSets = resolveFilterSets(urlFiltersValue);
    const currentFilterSetsString = JSON.stringify(filterSets);

    if (urlFiltersValue === currentFilterSetsString || (!urlFiltersValue && isEmpty(filterSets))) {
      return;
    }

    setFilterSetsState(urlFilterSets);

    if (sessionStorageKey) {
      try {
        if (isEmpty(urlFilterSets)) {
          localStorage.removeItem(sessionStorageKey);
        } else {
          localStorage.setItem(sessionStorageKey, urlFiltersValue);
        }
      } catch (e) {
        console.error("Error saving filters to session storage", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterSets and queryParams intentionally excluded to prevent infinite loops; this effect syncs URL → state
  }, [urlFiltersValue, useUrlParams, sessionStorageKey, filtersKey]);

  const setFilterSets = (newFilterSets: FilterSets<FilterCategory>, externalParams?: URLSearchParams) => {
    const params = externalParams ?? (useUrlParams ? new URLSearchParams(queryParams) : null);
    if (params) {
      if (Object.keys(newFilterSets).length === 0) {
        params.delete(filtersKey);
      } else {
        params.set(filtersKey, JSON.stringify(newFilterSets));
      }
    }

    if (sessionStorageKey) {
      try {
        if (Object.keys(newFilterSets).length === 0) {
          localStorage.removeItem(sessionStorageKey);
        } else {
          localStorage.setItem(sessionStorageKey, JSON.stringify(newFilterSets));
        }
      } catch (e) {
        console.error("Error saving filters to session storage", e);
      }
    }

    onFilterSetChange?.(newFilterSets, params ?? new URLSearchParams());
    setFilterSetsState(newFilterSets);
    if (params && !externalParams) {
      setQueryParams(params);
    }
  };

  return {
    filterSets,
    setFilterSets,
    filtersKey,
    isRestoringStoredFilterSets,
  };
};
